import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { TypeAnimation } from "react-type-animation";
import { dealItems, implementationItems } from "../pages/Services";
import Breadcrumb from "../components/Breadcrumb";
import AOS from "aos";
import "aos/dist/aos.css";

const ServiceDetail = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  const { id } = useParams();
  const navigate = useNavigate();

  const service = [...dealItems, ...implementationItems].find(
    (item) => item.id === id,
  );

  const words = service?.title?.split(" ") || [];
  const mid = Math.ceil(words.length / 2);
  const firstHalf = words.slice(0, mid).join(" ");
  const secondHalf = words.slice(mid).join(" ");

  const serviceCards = {
    "pe-pc-entry-exit": [
      {
        id: 1,
        image: "/assets/service/the-first-180-days.png",
        title: "The First 180 Days ",
        subtitle: "Value creation levers don't execute themselves.",
        points: [
          "Structured 180-day plan built from ground up",
          "Clear owners across every workstream",
          "Weekly tracking until the plan is reality",
        ],
      },
      {
        id: 2,
        image: "/assets/service/portfolio-reporting-and-governance.png",
        title: "Portfolio Reporting & Governance ",
        subtitle: "What gets measured gets managed.",
        points: [
          "MIS frameworks and performance dashboards",
          "Real-time view of portfolio health",
          "Early warning systems - not just quarterly compliance",
        ],
      },
      {
        id: 3,
        image: "/assets/service/exit-readiness.png",
        title: "Exit Readiness",
        subtitle:
          "Exits that go wrong had problems long before a buyer appeared.",
        points: [
          "Business story, disclosures, and DD preparation",
          "Legal & Tax structuring - strategy & execution",
          "The right preparation adds tangible value, speed and reduces deal friction",
        ],
      },
      {
        id: 4,
        image: "/assets/service/sign2seal.png",
        title: "Sign2Seal",
        subtitle: "Every day between signing and closing is capital at risk.",
        points: [
          "Full ownership of the closing process",
          "CP tracking, regulatory filings, stakeholder coordination",
          "Deal team stays focused. IRR stays protected.",
        ],
      },
    ],
    "ma-corporates": [
      {
        id: 1,
        image: "/assets/service/ma-execution.png",
        title: "M&A Execution",
        subtitle:
          "The gap between intent and close is where value is made or lost.",
        points: [
          "Ownership of evaluation and decision making",
          "M&A Project management expertise",
          "See-through deal - end to end",
        ],
      },
      {
        id: 2,
        image: "/assets/service/diligence-ownership.png",
        title: "Diligence Ownership",
        subtitle: "Good diligence is about pace as much as findings.",
        points: [
          "Financial, operational, and tax diligence ownership",
          "Multi-party process managed without losing momentum",
          "Nothing material gets missed",
        ],
      },
      {
        id: 3,
        image: "/assets/service/structuring-and-negotiations.png",
        title: "Structuring & Negotiations",
        subtitle: "The terms at close determine the value you walk away with.",
        points: [
          "Transaction structuring and deal protections",
          "Reps, warranties, indemnities, and tax positions",
          "Valued extension to a stretched deal team",
        ],
      },
      {
        id: 4,
        image: "/assets/service/post-deal-integration.png",
        title: "Post-Deal Integration",
        subtitle:
          "A clean close can still underdeliver without the right integration.",
        points: [
          "Unleashing the synergies of integration",
          "Managing conflicts and reducing friction",
          "Governance and reporting alignment from day one",
          "Foundations built to last, not patched together",
        ],
      },
    ],
    "family-offices": [
      {
        id: 1,
        image: "/assets/service/independent-evaluation.png",
        title: "Independent Evaluation",
        subtitle: "When it's family capital, a bad decision is personal.",
        points: [
          "Business quality, management depth, scalability",
          "Independent view beyond the information memorandum",
          "Conflict-free. Clear-eyed.",
        ],
      },
      {
        id: 2,
        image: "/assets/service/financial-analysis-and-valuation.png",
        title: "Financial Analysis & Valuation",
        subtitle: "A compelling story needs rigorous numbers.",
        points: [
          "Financial models and valuations",
          "Downside scenarios stress-tested",
          "Know exactly what you're risking, not just the upside",
        ],
      },
      {
        id: 3,
        image: "/assets/service/transaction-support.png",
        title: "Transaction Support",
        subtitle: "You need someone whose only priority is your outcome.",
        points: [
          "Diligence coordination and risk flagging",
          "Negotiation support and documentation review",
          "From first look to signing",
        ],
      },
      {
        id: 4,
        image: "/assets/service/post-investment-monitoring.png",
        title: "Post-Investment Monitoring",
        subtitle: "Investing is the beginning, not the end.",
        points: [
          "MIS and reporting frameworks post-close",
          "Early warning signals tracked actively",
          "Family capital managed, not just held",
        ],
      },
    ],
    "capital-markets": [
      {
        id: 1,
        image: "/assets/service/ipo-project-leadership.png",
        title: "IPO Project Leadership",
        subtitle: "The most complex transaction a company will run.",
        points: [
          "Leadership by executioners (IPO experienced), not consultants",
          "Knowledge led Project Management Office (PMO)",
          "Strong ecosystems with bankers, lawyers & IPO service providers",
          "End to end ownership from kick-off to listing day",
        ],
      },
      {
        id: 2,
        image: "/assets/service/drhp-and-regulatory-process.png",
        title: "DRHP & Regulatory Process",
        subtitle: "On-time approvals need more than good lawyers.",
        points: [
          "DRHP preparation and filing",
          "SEBI and exchange query management",
          "Capital structure and shareholder approval handling",
        ],
      },
      {
        id: 3,
        image: "/assets/service/investor-narrative-and-positioning.png",
        title: "Investor Narrative & Positioning",
        subtitle: "How you're presented determines how you're valued.",
        points: [
          "Business story and non-GAAP metrics",
          "Peer benchmarking and roadshow deck",
          "Tight, credible, consistently told",
        ],
      },
      {
        id: 4,
        image: "/assets/service/roadshow-and-listing-execution.png",
        title: "Roadshow & Listing Execution",
        subtitle: "The final stretch is where momentum matters most.",
        points: [
          "Roadshow planning and investor coordination",
          "Escrow banker alignment and listing execution",
          "Governance frameworks ready for life as a listed company",
        ],
      },
    ],
    alignment: [
      {
        id: 1,
        image: "/assets/service/translating-strategy-into-action.png",
        title: "Translating Strategy into Action",
        subtitle: "Objectives without a plan stay objectives.",
        points: [
          "High-level goals broken into time-bound priorities",
          "Defined owners and trackable KPIs",
          "Business kept accountable week to week",
        ],
      },
      {
        id: 2,
        image: "/assets/service/leadership-and-governance-alignment.png",
        title: "Leadership & Governance Alignment",
        subtitle: "The best structures are the ones that actually get used.",
        points: [
          "Leadership aligned around shared accountability",
          "Board and committee frameworks built for the business",
          "Reporting cadences that stick",
        ],
      },
      {
        id: 3,
        image: "/assets/service/ownership-and-execution-frameworks.png",
        title: "Ownership & Execution Frameworks",
        subtitle: "Strategies fail when no one owns the detail.",
        points: [
          "Clear ownership across business, finance, and operations",
          "Three-year plan connected to this quarter's decisions",
          "Nothing falls through between reviews",
        ],
      },
      {
        id: 4,
        image: "/assets/service/performance-databooks.png",
        title: "Performance Databooks",
        subtitle: "No MIS. Reviews running off financial statements alone.",
        points: [
          "Ground-up databook built for a PE-backed legal tech firm",
          "Revenue cuts, pipeline, cash flows, receivables",
          "Became the sole reference point for every management review",
        ],
      },
    ],
    "business-analysis": [
      {
        id: 1,
        image: "/assets/service/business-diagnostics.png",
        title: "Business Diagnostics",
        subtitle: "Before you fix performance, you need to understand it.",
        points: [
          "Financial, operational, and commercial review",
          "What's working, what isn't, where the gaps are",
          "Highest-impact opportunities identified",
        ],
      },
      {
        id: 2,
        image: "/assets/service/kpi-frameworks-and-mis-dashboards.png",
        title: "KPI Frameworks & MIS Dashboards",
        subtitle:
          "Most reporting tells you what happened. Ours tells you what to do.",
        points: [
          "MIS and KPI frameworks built for decisions",
          "Structured around metrics that drive the business",
          "Not month-end compliance. Actionable insight.",
        ],
      },
      {
        id: 3,
        image: "/assets/service/benchmarking-and-competitive-analysis.png",
        title: "Benchmarking & Competitive Analysis ",
        subtitle: "Performance gaps are easier to close when you can see them.",
        points: [
          "Peer, market, and sector benchmarking",
          "Underperformance surfaced with data",
          "Strategy validated against real comparables",
        ],
      },
      {
        id: 4,
        image: "/assets/service/financial-modelling-and-scenario-analysis.png",
        title: "Financial Modelling & Scenario Analysis",
        subtitle: "Growth decisions need numbers behind them.",
        points: [
          "Unit economics and profitability analysis",
          "Cash flow modelling across products and entities",
          "Scenarios stress-tested, assumptions challenged",
        ],
      },
    ],
    execution: [
      {
        id: 1,
        image: "/assets/service/initiative-tracking-and-follow-through.png",
        title: "Initiative Tracking & Follow-Through",
        subtitle: "Plans stall when no one owns the detail.",
        points: [
          "Structured tracking with clear milestones",
          "Active follow-through, not status updates",
          "High-priority initiatives driven to completion",
        ],
      },
      {
        id: 2,
        image: "/assets/service/post-investment-operating-build.png",
        title: "Post-Investment Operating Build",
        subtitle:
          "The first months after close determine everything that follows.",
        points: [
          "CXO hiring and governance implementation",
          "Operating process improvement",
          "Foundations built in the window that matters most",
        ],
      },
      {
        id: 3,
        image: "/assets/service/budgeting-fpa-and-performance-reviews.png",
        title: "Budgeting, FP&A & Performance Reviews",
        subtitle: "An AOP that sits in a folder isn't an operating tool.",
        points: [
          "Budgeting and AOP embedded in business rhythm",
          "Actuals tracked against plan in real time",
          "Management accountable to what was agreed",
        ],
      },
      {
        id: 4,
        image: "/assets/service/hands-on-execution.png",
        title: "Hands-On Execution",
        subtitle: "Not observers. The team responsible for getting it done.",
        points: [
          "One point of accountability across all stakeholders",
          "Transactions, integrations, exits, distress situations",
          "We step in wherever ownership is needed most",
        ],
      },
    ],
  };
  const defaultCards = [
    {
      id: 1,
      image:
        "https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=400&q=80",
      title: "Strategic Alignment",
      subtitle: "",
      points: [
        "Align leadership around clear objectives",
        "Establish decision frameworks",
        "Create accountability structures",
        "Ensure investor communication",
      ],
    },
    {
      id: 2,
      image:
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80",
      title: "Analysis & Insights",
      subtitle: "",
      points: [
        "Conduct operational reviews",
        "Perform market mapping",
        "Analyze competitor landscape",
        "Identify value opportunities",
      ],
    },
    {
      id: 3,
      image:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&q=80",
      title: "Execution Excellence",
      subtitle: "",
      points: [
        "Apply project discipline",
        "Implement governance structure",
        "Track performance metrics",
        "Deliver milestones on time",
      ],
    },
    {
      id: 4,
      image:
        "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=400&q=80",
      title: "Continuous Improvement",
      subtitle: "",
      points: [
        "Monitor progress regularly",
        "Adapt strategies as needed",
        "Communicate transparently",
        "Celebrate achievements",
      ],
    },
  ];

  const cards = serviceCards[id] || defaultCards;

  // ── Active card driving the right-side stack + left-side accordion ──
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const cardSectionRef = useRef(null);

  useEffect(() => {
    setActiveCardIndex(0);
  }, [id]);

  const handleCardSelect = (index) => {
    setActiveCardIndex(index);

    // On mobile the card stack sits below the title list, out of view.
    // Bring it into view so the person can actually see the card they
    // just selected instead of it silently changing off-screen.
    if (
      typeof window !== "undefined" &&
      window.innerWidth <= 768 &&
      cardSectionRef.current
    ) {
      cardSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Only two visible slots: the active card up front, the next one
  // peeking behind it. Everything else stays fully hidden.
  const getCardSlot = (index) => {
    const len = cards.length;
    if (index === activeCardIndex) return "front";
    if (index === (activeCardIndex + 1) % len) return "behind";
    return "hidden";
  };

  if (!service) {
    return (
      <div style={{ padding: "50px", textAlign: "center" }}>
        <h2>Service not found</h2>
        <button onClick={() => navigate("/services")}>Back to Services</button>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb />
      <div className="service-detail-page">
        <div className="service-detail-main">
          <div className="museum-wrap">
            <div className="service-detail-two-col">
              {/* ── LEFT: description + accordion title list ── */}
              <div className="sd-left" data-aos="fade-right">
                <h1>
                  {firstHalf}{" "}
                  <span className="highlight">
                    <TypeAnimation
                      key={id}
                      sequence={[secondHalf]}
                      speed={250}
                      cursor={false}
                    />
                  </span>
                </h1>
                <p className="service-detail-description">
                  {service.description}
                </p>

                <nav
                  className="sd-title-list"
                  aria-label="Service capabilities"
                >
                  {cards.map((card, index) => {
                    const isActive = activeCardIndex === index;
                    return (
                      <div
                        key={card.id}
                        className={`sd-title-item${isActive ? " sd-title-item--active" : ""}`}
                      >
                        <button
                          className="sd-title-header"
                          onClick={() => handleCardSelect(index)}
                          aria-expanded={isActive}
                        >
                          <span className="sd-title-chevron" aria-hidden="true">
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                d="M6 9l6 6 6-6"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          <span className="sd-title-text">{card.title}</span>
                          {/*<span className="sd-title-chevron" aria-hidden="true">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path
                                d="M6 9l6 6 6-6"
                                stroke="currentColor"
                                strokeWidth="2.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>*/}
                        </button>

                        <div className="sd-title-accordion-wrap">
                          <div className="sd-title-accordion-inner">
                            {card.subtitle && (
                              <p className="sd-title-subtitle">
                                {card.subtitle}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </nav>
              </div>

              {/* ── RIGHT: 2-visible card stack, same visual language as
                     the Strategy Implementation carousel (background image,
                     dark overlay, content on top) but click-driven, no autoplay ── */}
              <div
                className="sd-right"
                data-aos="fade-left"
                ref={cardSectionRef}
              >
                <div className="sd-card-stack-wrapper">
                  <div className="sd-card-stack">
                    {cards.map((card, index) => {
                      const slot = getCardSlot(index);
                      return (
                        <div
                          key={card.id}
                          className={`sd-stack-card sd-stack-card--${slot}`}
                          style={{ backgroundImage: `url('${card.image}')` }}
                          onClick={() =>
                            slot === "behind" && handleCardSelect(index)
                          }
                          role={slot === "behind" ? "button" : undefined}
                          tabIndex={slot === "behind" ? 0 : -1}
                          aria-label={
                            slot === "behind" ? `Show ${card.title}` : undefined
                          }
                        >
                          <div className="sd-stack-card-overlay" />
                          <div className="sd-stack-card-body">
                            <h3>{card.title}</h3>
                            <ul className="sd-stack-points">
                              {card.points.map((point, idx) => (
                                <li key={idx} className="sd-stack-point">
                                  <span>{point}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Dot indicators, mirrors the Strategy Implementation carousel */}
                  <div className="sd-dots">
                    {cards.map((_, i) => (
                      <button
                        key={i}
                        className={`sd-dot${i === activeCardIndex ? " sd-dot--active" : ""}`}
                        onClick={() => handleCardSelect(i)}
                        aria-label={`Show card ${i + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .service-detail-page {
          background: var(--bg-main, #fffdf6);
        }

        .service-detail-main {
          padding: 88px 0 80px;
        }

        .museum-wrap {
          max-width: min(1400px, 90%);
          margin: 0 auto;
          padding: 0 clamp(16px, 3vw, 40px);
        }

        /* ── Two column layout ─────────────────────────── */
        .service-detail-two-col {
          display: grid;
          grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
          gap: clamp(32px, 5vw, 72px);
          align-items: start;
        }

        /* ── LEFT column ───────────────────────────────── */
        .sd-left h1 {
          font-family: var(--font-heading);
          font-size: clamp(2rem, 4vw, 3rem);
          line-height: 1.2;
          margin-bottom: 18px;
          color: var(--color-dark, #1a1a1a);
        }

        .sd-left h1 .highlight {
          color: var(--color-orange, #e87722);
          font-style: italic;
        }

        .service-detail-description {
          font-family: var(--font-body);
          font-size: 1.05rem;
          line-height: 1.65;
          color: var(--text-sub);
          max-width: 560px;
          margin-bottom: 36px;
        }

        .sd-title-list {
          display: flex;
          flex-direction: column;
        }

        .sd-title-header {
          width: 100%;
          background: none;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 18px 4px;
          text-align: left;
          font-family: var(--font-heading);
        }

        .sd-title-num {
          font-size: 13px;
          font-weight: 600;
          color: var(--color-orange, #e87722);
          min-width: 24px;
        }

        .sd-title-text {
          flex: 1;
          font-size: clamp(1rem, 1.4vw, 1.15rem);
          font-weight: 400;
          color: var(--color-dark, #1a1a1a);
          transition: color 0.25s ease;
        }

        .sd-title-item--active .sd-title-text {
          color: var(--color-orange, #e87722);
        }

        .sd-title-chevron {
          display: flex;
          color: var(--color-dark, #1a1a1a);
          transition:
            transform 0.35s ease,
            color 0.25s ease;
        }

        .sd-title-item--active .sd-title-chevron {
          transform: rotate(180deg);
          color: var(--color-orange, #e87722);
        }

        /* Accordion using the grid-rows technique for smooth height animation */
        .sd-title-accordion-wrap {
          display: grid;
          grid-template-rows: 0fr;
          transition: grid-template-rows 0.4s ease;
        }

        .sd-title-item--active .sd-title-accordion-wrap {
          grid-template-rows: 1fr;
        }

        .sd-title-accordion-inner {
          overflow: hidden;
        }

        .sd-title-subtitle {
          font-family: var(--font-body);
          font-size: 0.95rem;
          color: var(--text-sub);
          padding: 0 4px 10px;
        }

        .sd-title-points {
          list-style: none;
          margin: 0;
          padding: 0 4px 20px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .sd-title-point {
          font-family: var(--font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: #363739;
          position: relative;
          padding-left: 16px;
        }

        .sd-title-point::before {
          content: "•";
          position: absolute;
          left: 0;
          color: var(--color-orange, #e87722);
          font-weight: bold;
        }

        /* ── RIGHT column: 2-visible card stack ───────────── */
        .sd-right {
          position: sticky;
          top: 100px;
          padding-right: clamp(24px, 4vw, 48px);
          /* ── NEW: Vertically center the cards ── */
          display: flex;
          align-items: center;
          min-height: 100%;
        }

        .sd-card-stack-wrapper {
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .sd-card-stack {
          position: relative;
          width: 100%;
          max-width: 440px;
          height: 350px;
          margin: 0 auto;
          /* room on the right for the peeking card, same idea as impl-stage */
          overflow: visible;
        }

        /* Same positioning language as .impl-card--pos-*: cards live in the
           same box and slide/scale into place instead of stacking diagonally.
           Only two are ever visible — front (center) and behind (peeking on
           the right) — everything else sits fully hidden off to the side. */
        .sd-stack-card {
          position: absolute;
          inset: 0;
          border-radius: 20px;
          overflow: hidden;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          transition:
            transform 0.56s cubic-bezier(0.65, 0, 0.35, 1),
            opacity 0.45s ease,
            filter 0.45s ease;
        }

        .sd-stack-card--front {
          transform: translateX(0) scale(1);
          z-index: 3;
          opacity: 1;
          cursor: default;
        }

        .sd-stack-card--behind {
          transform: translateX(92px) scale(0.93);
          z-index: 2;
          opacity: 1;
          filter: brightness(0.94);
          cursor: pointer;
        }

        .sd-stack-card--behind:hover {
          transform: translateX(84px) scale(0.94);
          filter: brightness(1);
        }

        .sd-stack-card--hidden {
          transform: translateX(160px) scale(0.82);
          z-index: 1;
          opacity: 0;
          pointer-events: none;
        }

        .sd-stack-card-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            rgba(15, 15, 15, 0.1) 0%,
            rgba(15, 15, 15, 0.5) 40%,
            rgba(15, 15, 15, 0.88) 68%,
            rgba(15, 15, 15, 0.94) 100%
          );
          z-index: 1;
        }

        .sd-stack-card-body {
          position: relative;
          z-index: 2;
          padding: 28px 26px 30px;
          display: flex;
          flex-direction: column;
          /* extra dark backdrop scoped to the text block itself, so
             contrast holds no matter how tall the points list gets */
          background: linear-gradient(
            180deg,
            rgba(10, 10, 10, 0) 0%,
            rgba(10, 10, 10, 0.55) 20%,
            rgba(10, 10, 10, 0.85) 100%
          );
          margin-top: -60px;
        }

        .sd-stack-card-num {
          font-family: var(--font-heading);
          font-size: 13px;
          font-weight: 700;
          color: var(--color-orange, #e87722);
          margin-bottom: 8px;
        }

        .sd-stack-card-body h3 {
          font-family: var(--font-heading);
          font-size: 1.3rem;
          color: #fff;
          margin-bottom: 8px;
        }

        .sd-stack-card-subtitle {
          font-family: var(--font-body);
          font-size: 0.95rem;
          font-style: italic;
          color: var(--color-orange, #e87722);
          margin-bottom: 14px;
        }

        .sd-stack-points {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .sd-stack-point {
          font-family: var(--font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: rgba(255, 255, 255, 0.9);
          position: relative;
          padding-left: 16px;
        }

        .sd-stack-point::before {
          content: "•";
          position: absolute;
          left: 0;
          color: var(--color-orange, #e87722);
          font-weight: bold;
        }

        /* Behind card keeps its title readable (so it's clear which card
           it is and that it's clickable) but hides the denser subtitle/
           points so text doesn't collide with the front card overlapping it */
        .sd-stack-card--behind .sd-stack-card-body {
          opacity: 1;
        }

        .sd-stack-card--behind .sd-stack-card-subtitle,
        .sd-stack-card--behind .sd-stack-points {
          display: none;
        }

        /* Dots */
        .sd-dots {
          display: flex;
          justify-content: center;
          gap: 10px;
          margin-top: 24px;
        }

        .sd-dot {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          border: none;
          background: rgba(54, 55, 57, 0.25);
          cursor: pointer;
          padding: 0;
          transition: all 0.3s ease;
        }

        .sd-dot--active {
          width: 24px;
          border-radius: 5px;
          background: var(--color-orange, #e87722);
        }

        /* ── Responsive ─────────────────────────────────────────────
           Breakpoints mirror the ones already used across Services.css
           (992 / 769–991 / 641–768 / 481–640 / ≤480, + landscape) so this
           section falls in line with the rest of the site instead of
           introducing its own scale. ── */

        /* Small Laptop (992px – 1200px) */
        @media (min-width: 992px) and (max-width: 1200px) {
          .service-detail-two-col {
            gap: 40px;
          }

          .sd-card-stack {
            max-width: 400px;
            height: 350px;
          }
        }

        /* Tablet (769px – 991px) — still two columns, just tighter */
        @media (min-width: 769px) and (max-width: 991px) {
          .service-detail-main {
            padding: 48px 0 60px;
          }

          .service-detail-two-col {
            gap: 32px;
          }

          .sd-card-stack {
            max-width: 340px;
            height: 400px;
          }

          .sd-right {
            padding-right: clamp(16px, 3vw, 32px);
            align-items: flex-start;
          }
        }

        /* Tablet Portrait and below (≤768px) — single column, cards
           follow the title list in normal document order */
        @media (max-width: 768px) {
          .service-detail-main {
            padding: 38px 0 60px;
          }

          .service-detail-two-col {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .sd-left h1 {
            font-size: 1.8rem;
          }

          .service-detail-description {
            font-size: 0.95rem;
          }

          .sd-right {
            position: static;
            padding-right: 0;
            /* offsets the scrollIntoView() target so it doesn't land
               flush under a fixed header when a title is tapped */
            scroll-margin-top: 88px;
            /* .sd-right is no longer sticky at this breakpoint, so it's
               safe to clip here without breaking position: sticky */
            overflow-x: hidden;
            align-items: center;
          }

          .sd-card-stack {
            height: 350px;
            max-width: 380px;
            overflow: hidden;
          }

          .sd-stack-card--behind {
            transform: translateX(56px) scale(0.93);
          }

          .sd-stack-card--behind:hover {
            transform: translateX(50px) scale(0.94);
          }

          .sd-stack-card--hidden {
            transform: translateX(100px) scale(0.82);
          }
        }

        /* Mobile (481px – 640px) */
        @media (min-width: 481px) and (max-width: 640px) {
          .sd-card-stack {
            height: 350px;
            max-width: 340px;
          }
        }

        /* Small Mobile (≤480px) */
        @media (max-width: 480px) {
          .service-detail-main {
            padding-top: 28px;
          }

          .sd-left h1 {
            font-size: 1.6rem;
          }

          .service-detail-description {
            font-size: 0.9rem;
          }

          .sd-right {
            scroll-margin-top: 68px;
          }

          .sd-card-stack {
            height: 350px;
            max-width: 300px;
          }

          .sd-stack-card--behind {
            transform: translateX(40px) scale(0.93);
          }

          .sd-stack-card--behind:hover {
            transform: translateX(36px) scale(0.94);
          }

          .sd-stack-card--hidden {
            transform: translateX(80px) scale(0.82);
          }

          .sd-stack-card-body {
            padding: 18px 18px 20px;
          }

          .sd-stack-card-body h3 {
            font-size: 1.1rem;
          }

          .sd-stack-point {
            font-size: 0.82rem;
          }
        }

        /* Mobile Landscape */
        @media (max-width: 900px) and (orientation: landscape) {
          .sd-card-stack {
            height: 320px;
            max-width: 360px;
            overflow: hidden;
          }

          .sd-stack-card--behind {
            transform: translateX(48px) scale(0.93);
          }

          .sd-stack-card--hidden {
            transform: translateX(90px) scale(0.82);
          }

          .sd-stack-points {
            gap: 6px;
          }
        }
      `}</style>
    </>
  );
};

export default ServiceDetail;
